// @ts-nocheck
import { useContext, useEffect, useState } from 'react';
import { Box, Text, Button as MantineButton } from '@mantine/core';
import Fetch from '@/utils/Fetch';
import CustomerContactLocationSelector from '@/components/selectors/customer/customer-contact-location-selector';
import useInitialTimeout from '@/hooks/useInitialTimeout';
import ToastContext from '@/utils/toast-context';
import SCModal from '@/PageComponents/Modal/SCModal';
import styles from './customer-change-modal.module.css';

interface CustomerChangeModalProps {
    open: boolean;
    onClose: () => void;
    itemLabel: string;
    moduleLabel: string;
    description?: string;
    checkUrl: string;
    changeUrl: string;
    itemIdKey: string;
    itemId: string;
    moduleType: number;
    excludedCustomerID?: string;
    accessStatus?: any;
    onCustomerChanged: (result: any) => void;
    extraPayload?: (customer: any, contact: any, location: any) => object;
    renderExtra?: (params: { selectedCustomer: any; selectedContact: any; selectedLocation: any }) => any;
    cannotChangePrefix?: string;
}

const CustomerChangeModal = ({
    open,
    onClose,
    itemLabel,
    moduleLabel,
    description,
    checkUrl,
    changeUrl,
    itemIdKey,
    itemId,
    moduleType,
    excludedCustomerID,
    accessStatus,
    onCustomerChanged,
    extraPayload,
    renderExtra,
    cannotChangePrefix,
}: CustomerChangeModalProps) => {

    const toast = useContext(ToastContext);

    const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
    const [selectedContact, setSelectedContact] = useState<any>(null);
    const [selectedLocation, setSelectedLocation] = useState<any>(null);
    const [componentLoaded, setComponentLoaded] = useState(false);
    const [componentState, setComponentState] = useState<any>(null);
    const [failureReason, setFailureReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<any>({});

    useInitialTimeout(50, () => setComponentLoaded(true));

    useEffect(() => {
        if (!open) return;
        setComponentState(null);
        Fetch.get({ url: checkUrl })
            .then((result: any) => {
                setComponentState(result.Result);
                setFailureReason(result.Message || result.message || result.serverMessage || 'An unexpected error occurred. Please try again.');
            })
            .catch(() => {
                setComponentState(false);
                setFailureReason('An unexpected error occurred. Please try again.');
            });
    }, [open]);

    useEffect(() => {
        setSelectedContact(null);
        setSelectedLocation(null);
    }, [selectedCustomer]);

    const handleUpdate = async () => {
        const validationErrors: any = {};
        if (!selectedCustomer) validationErrors.Customer = 'Customer is required';
        if (!selectedContact) validationErrors.Contact = 'Contact is required';
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        setSubmitting(true);
        const result = await Fetch.post({
            url: changeUrl,
            params: {
                [itemIdKey]: itemId,
                CustomerID: selectedCustomer.ID,
                ContactID: selectedContact.ID,
                LocationID: selectedLocation ? selectedLocation.ID : null,
                ...(extraPayload ? extraPayload(selectedCustomer, selectedContact, selectedLocation) : {}),
            },
        });
        setSubmitting(false);

        if (result?.ID) {
            toast.setToast({ message: 'Customer change was successful', show: true, type: 'success' });
            onClose();
            onCustomerChanged(result);
        }
    };

    const canChange = componentState === true;
    const cannotChange = componentState === false;

    return (
        <SCModal
            open={open}
            onClose={onClose}
            size="38rem"
            p={0}
            modalProps={{
                className: 'sc-customer-change-modal',
                onClick: (e: any) => e.stopPropagation(),
                closeOnClickOutside: false,
                closeOnEscape: false,
            }}
        >
            <Box className={styles.container}>
                <Box className={styles.title}>
                    {cannotChange ? `Cannot Change Customer for ${itemLabel}` : `Change Customer for ${itemLabel}`}
                </Box>

                {canChange && componentLoaded && (
                    <>
                        {description && (
                            <Box className={styles.infoBox}>{description}</Box>
                        )}
                        <CustomerContactLocationSelector
                            compactView={true}
                            inputErrors={errors}
                            selectedCustomer={selectedCustomer}
                            selectedContact={selectedContact}
                            selectedLocation={selectedLocation}
                            setSelectedContact={setSelectedContact}
                            setSelectedCustomer={setSelectedCustomer}
                            setSelectedLocation={setSelectedLocation}
                            excludedCustomerID={excludedCustomerID ?? null}
                            module={moduleType}
                            accessStatus={accessStatus}
                            canChangeCustomer={true}
                            canEdit={true}
                            backButtonText={'Back to Customer Update'}
                            showSearchIcon={true}
                            extraClasses={undefined} sendEmail={undefined} setSendEmail={undefined}
                            sendSMS={undefined} setSendSMS={undefined} cypressCustomer={undefined}
                            cypressContact={undefined} cypressLocation={undefined}
                        />
                        {renderExtra && renderExtra({ selectedCustomer, selectedContact, selectedLocation })}
                    </>
                )}

                {cannotChange && (
                    <Text className={styles.failureReason}>
                        {cannotChangePrefix && <>{cannotChangePrefix}<br /></>}
                        {failureReason}
                    </Text>
                )}

                <hr className={styles.divider} />

                <Box className={`${styles.row} ${styles.justifyEnd}`} style={{ gap: '0.75rem' }}>
                    {(canChange || cannotChange) && (
                        <MantineButton variant="outline" onClick={onClose}>
                            Cancel
                        </MantineButton>
                    )}
                    {canChange && (
                        <MantineButton onClick={handleUpdate} disabled={submitting}>
                            {submitting ? 'Changing Customer...' : 'Change Customer'}
                        </MantineButton>
                    )}
                </Box>
            </Box>
        </SCModal>
    );
};

export default CustomerChangeModal;
